
import 'jquery';
import * as angular from 'angular';

import { NavSettingsService } from '@common/services/settingsservice';
import { DialogService, IMessageIgnore } from '@services/dialogservice';
import { fn as fn, IInfOperation, downloadFile, base64toBlob } from '@common/utils';
import resolveToError = fn.resolveToError;
import UISafe = fn.UISafe;
import { RwfAppSettings } from '@common/rwf/rwf.appsettings';
import { CompatibilityService, IDeferred } from '@common/services/compatibilityservice';
import { IKWFSignDoc, IKWFSignOptions } from '@common/services/DialogService';
import { csBridge, IBridgeParams, IBrigdeOptions } from '../extern/csBridge';
import { IKWFRoute } from './KWFMain';
import { ThrowGracefullError, ReAuthenticate, toBase64 } from '@common/rwf';
import { Observable } from './nav';
import { Bind } from './navkendo';

/** Signature Sign Scope .... */
interface IKWFSignScope extends ng.IScope {
    UIProgress: (arg) => void;
    UIFinish: (arg) => void;
    GetSignatureInfo(): IKWFSignDoc[];
    ID: any;
    SignOptions: IKWFSignOptions;
}

/** Intarsys Decoarator Args */
export interface IDecoratorArgs {
    field: {
        create: true,
        position: string,
        size: string,
        pageRange: string

    };
    textScaleWhen?: "always";
    textScaleProportional?: boolean;
    textVAlign: "center";
    textHAlign: "right";
    layout: "textRightOfIcon";
    text: string;
    icon: {
        name: string;
        content: string
    };
    iconScaleWhen: "always";
    iconScaleProportional: boolean;
    iconVAlign: "center";
    iconHAlign: "left";
}

/** Signature Dimension Info */
const S_Dim = {
    Height: 50,
    Width: 180,
    ColStarts: [20, 205, 390], // 20 : left start, 5 : gap
    /** Position for given amount of previous signatures */
    GetPos: (prevSigCount: number = 0) => {
        var colstarts = S_Dim.ColStarts,
            col = prevSigCount % colstarts.length,
            row = Math.floor(prevSigCount / colstarts.length);
        return "" + colstarts[col] + "*" + (20 + (row * S_Dim.Height));
    },
    GetSize: () => "" + S_Dim.Width + "*" + S_Dim.Height,
    GetIconData: ($c: CompatibilityService, prevSigCount: number) => GetSignIcon($c, prevSigCount)
};

const getDocDecoratorField = (prevCount?: number) => ({
    create: true,
    position: S_Dim.GetPos(prevCount || 0),
    size: S_Dim.GetSize(),
    pageRange: "last",
});

const getDocDecoratorArgs = ($c: CompatibilityService, PreviousSignatureCount: number, text: string) => GetSignIcon($c, PreviousSignatureCount).then(icon => {
    return <IDecoratorArgs>{
        field: getDocDecoratorField(PreviousSignatureCount),
        textScaleWhen: "always",
        textScaleProportional: true,
        textVAlign: "center",
        textHAlign: "right",
        layout: "textRightOfIcon",
        text: text,
        icon: {
            name: "signature.png",
            content: icon
        },
        iconScaleWhen: "always",
        iconScaleProportional: true,
        iconVAlign: "center",
        iconHAlign: "left",
    };
});

/** Modal Sign Document(s) : Result is undefined if user cancelled, other errors get passed throug promise (might be 'uncaught'cancellation!) */
export function ModalSignDocument<TSignDoc extends IKWFSignDoc>(this: void, $injector: ng.auto.IInjectorService, signOptions: IKWFSignOptions, signDocs: PromiseLike<TSignDoc[]> | any[]) {
    const $c = $injector.get("CompatibilityService");
    const $rwfBootstrap = $c.resolve("Rwf")("RwfBootstrap");
    return $rwfBootstrap.Resolve.then(rwf => {
        const gears = rwf.RwfSettings.Gears;

        if (signOptions.ProGOV === true) {
            modalSign = $c.when(signDocs).then((resolvedDocs: IKWFSignDoc[]) =>
                ModalSignViaProGOV(signOptions, resolvedDocs, $c)
            ) as PromiseLike<TSignDoc[]>;
        }
        else if (signOptions.Bridge?.LicenseBase64) {
            modalSign = $c.when(signDocs).then((resolvedDocs: IKWFSignDoc[]) =>
                ModalSignViaBridge(signOptions, resolvedDocs, $c)
            ) as PromiseLike<TSignDoc[]>;
        }
        else if (rwf?.InfomaClient) {
            modalSign = $c.when(signDocs).then(resolvedDocs => {
                return applet.ResolveAppletDocInfos($c, resolvedDocs, signOptions, {}).then(resolvedDocs => {
                    return rwf.InfomaClient.asyncModalSignDocuments(signOptions, resolvedDocs).then(signedDocs => {
                        return signedDocs.map((s, idx) => {
                            const prev = resolvedDocs[idx],
                                changed: Partial<IKWFSignDoc> = {
                                    Base64Content: s.Base64Content,
                                    PreviousSignatureCount: (prev.PreviousSignatureCount >= 0) ? (prev.PreviousSignatureCount + 1) : undefined
                                };
                            return <TSignDoc>$.extend({}, prev, changed);
                        });
                    });
                });
            });
        }
        else if (gears && gears.Url) {
            let sinstance = new Gears.GearsConnectorService($injector, gears);
            modalSign = sinstance.ModalSignDocument<TSignDoc>(signOptions, signDocs);
        } else {
            let ainstance = $injector.instantiate(applet.AppletConnector);
            var modalSign = ainstance.ModalSignDocument<TSignDoc>(signOptions, signDocs);
        }

        return modalSign.then(fn.identity, err => {
            return $c.when<any>(!fn.error.IsCancel || $c.resolve("DialogService").ShowError(err)).then(() => undefined);
        });
    });
}

export function GroupDocs<TSignDoc extends IKWFSignDoc>(signDocs: TSignDoc[]) {
    const getGroupState = (item: TSignDoc) => ({
        Info: item.Info, PreviousSignatureCount: item.PreviousSignatureCount, Text: item.Text, IconDataBase64: item.IconDataBase64
    });
    return fn.DictToArray(fn.GroupBy(signDocs, item => JSON.stringify(getGroupState(item))));
}

export async function ModalSignViaBridge<TSignDoc extends IKWFSignDoc>(this: void, signOptions: IKWFSignOptions, signDocs: TSignDoc[],
    $c?: CompatibilityService): Promise<TSignDoc[]> {

    const loadIconData = $c.all(signDocs.map(item => GetSignIcon($c, item.PreviousSignatureCount).then(iconData => item.IconDataBase64 = iconData)));

    try {
        var bridge = await EnsureBridge({
            host: "127.0.0.1",
            select: await signOptions.Bridge?.Select,
            license: {
                content: await signOptions.Bridge?.LicenseBase64
            }
        }, $c);

        await loadIconData;
        const bridgletParams = {
            factory: 'de.intarsys.cloudsuite.bridge.control.workflow.SignatureBridglet',
            args: {
                // “principal.qualified” ??
                principalFilter: signOptions?.SerialNo ? `principal.serialNumber=='${signOptions.SerialNo}'` : "principal.supportsSignature",
                deviceProvider: {
                    composite: {
                        accumulate: true
                    }
                },
                documents: signDocs.map(doc => ({
                    name: doc.Name,
                    content: doc.Base64Content,
                    tags: {
                        args: {
                            documentSigner: {
                                args: {
                                    decorator: {
                                        factory: "com.cabaret.security.method.pdf.signing.decorator.ExtendedDecoratorFactory",
                                        args: {
                                            textScaleWhen: "always",
                                            textScaleProportional: true,
                                            textVAlign: "center",
                                            textHAlign: "right",
                                            layout: "textRightOfIcon",
                                            text: doc.Text || "",
                                            icon: doc.IconDataBase64 && {
                                                name: "srr.png",
                                                content: doc.IconDataBase64
                                            },
                                            iconScaleWhen: "always",
                                            iconScaleProportional: true,
                                            iconVAlign: "center",
                                            iconHAlign: "left",
                                        }
                                    },
                                    additionalInfoSet: doc.Info,
                                    field: getDocDecoratorField(doc.PreviousSignatureCount || 0)
                                }
                            },
                        }
                    },
                })
                )
            }
        };
        var bridglet = await bridge.createBridglet(bridgletParams);

        var pResult = await new Promise<TSignDoc[]>((res, rej) => {
            bridglet.addEventListener('ok', async () => {
                try {
                    var result: { signatures?: string[], signature?: string } = await bridglet.wantResultValue();
                    var adjusted = (result.signatures || [result.signature]).map((s, idx) => {
                        const prev = signDocs[idx],
                            changed: Partial<IKWFSignDoc> = {
                                Base64Content: s,
                                PreviousSignatureCount: (prev.PreviousSignatureCount >= 0) ? (prev.PreviousSignatureCount + 1) : undefined
                            };
                        return <TSignDoc>$.extend({}, prev, changed);
                    });
                    res(adjusted);
                } catch (err) {
                    rej(err);
                }
            });
            bridglet.addEventListener('cancel', () => rej(fn.error.ExplicitError("Abbruch durch Benutzer", true)));
            bridglet.addEventListener('fail', f => {
                const error = f?.event?.error;
                const code = error?.code ?? "??";
                const message = error?.message ?? "failed";
                rej(`${code}: ${message}`);
            });

            bridglet.enter();
        });
        $c?.tick();
        return pResult;
    } catch (err) {
        throw fn.resolveToError(err);
    } finally {
        $c?.tick();
        bridglet?.dispose();
        bridge?.dispose();
    }
}

export async function ModalSignViaProGOV<TSignDoc extends IKWFSignDoc>(this: void, signOptions: IKWFSignOptions, signDocs: TSignDoc[],
    $c: CompatibilityService): Promise<TSignDoc[]> {

    var proGOVAlias = signOptions.SerialNo;
    if (!proGOVAlias) {
        throw new Error("Für die Signatur ist die Hinterlegung Ihres Signatur-Aliases an Ihrem Workflowbenutzer notwendig!\r\n Wenden Sie sich bitte an Ihren Workflowadministrator!");
    }

    const signOps = signDocs.map(signDoc => ({
        SignDoc: signDoc,
        Result: "",
        SignedBase64Content: $c.post<{ Base64Content: string }>("infoma.rwf.web/ProGOV/SignDocument?" + fn.EncodeQueryData({ alias: proGOVAlias }), {
            Base64Content: signDoc.Base64Content
        }).then(s => {
            const result = s?.data.Base64Content;
            if (!result) {
                throw new Error("Got no result!")
            }
            return result;
        })
    }));

    for (var i = 0; i < signOps.length; i++) {
        signOps[i].Result = await signOps[i].SignedBase64Content;

    }
    return signOps.map((s, idx) => {
        const prev = s.SignDoc,
            changed: Partial<IKWFSignDoc> = {
                Base64Content: s.Result,
                PreviousSignatureCount: (prev.PreviousSignatureCount >= 0) ? (prev.PreviousSignatureCount + 1) : undefined
            };
        return <TSignDoc>$.extend({}, prev, changed);
    });
}

const SignLiveProductName = "Sign Live! CC cloud suite bridge 7.1";
async function EnsureBridge(bridgeParams: IBridgeParams, $c?: CompatibilityService) {
    // create a bridge stub
    let bridge = await csBridge.lib.createBridge(bridgeParams);

    try {
        bridge = await bridge.connect();
    } catch (err) {
        if (err?.code === "Bridge.NoAgent") {

            await csBridge.util.launchCSBridge(bridge);

            await new Promise(async (_res, _rej) => {
                let done = false;
                const res = (r) => { done = true; _res(r); };
                const rej = (r) => { done = true; _rej(r); };
                var counter = 0;
                const dlg = $c?.resolve("DialogService");
                var $wnd = await dlg?.ShowWindow({
                    controllerAs: "vm",
                    controller: [function ($scope) {
                        this["Cancel"] = () => {
                            rej(fn.error.ExplicitError("Abbruch", true));
                            $wnd.closeDialog();
                            bridge.dispose();
                        };
                        this["Product"] = SignLiveProductName;

                    }],
                    windowOptions: {
                        modal: true,
                        actions: []
                    },
                    template: `<div align="center" >Bitte starten Sie '${SignLiveProductName}'.<br ><br > Sollte Ihr Browser Sie nicht zum Öffnen von '${SignLiveProductName}' auffordern,<br > stellen Sie sicher, dass '${SignLiveProductName}' installiert ist!<div><br >` +
                        '<div align="center">' +
                        '<div>'
                });

                const check = () => {
                    try {
                        bridge.connect().then((r) => {
                            $wnd?.closeDialog();
                            res(r);
                        }, retry);
                    } catch (err) {
                        retry();
                    }
                };
                const retry = () => {
                    if (counter++ > 20 && !$wnd) {
                        rej("Signaturkomponente konnte nicht gestartet werden! Bitte stellen Sie sicher, dass 'Sign Live! CC cloud suite bridge' installiert und korrekt konfiguriert ist!");
                    } else if (!done) {
                        window.setTimeout(check, 2000);
                    }
                };
                check();

            });
        } else {
            throw err.code ? new Error(">" + err.code + ":" + err.message) : err;
        }
    }
    return bridge;
}

/** Retrieve Signature Icon Data As Base64 String */
export function GetSignIcon($c: CompatibilityService, prevSigCount: number) {
    var url = "Icons/Sign/Sign" + ((prevSigCount || 0) % 4) + ".png";
    return $c.get<string>("/infoma.Web.Nav/Base64Download?relativeUrl=" + url, { cache: true }).then(d => d.data, err => {
        throw new Error("'" + url + "' konnte nicht geladen werden: " + resolveToError(err));
    });
}

export namespace Gears {

    const SCHEMA_PREFIX = "urn:intarsys:names:conversation:1.0:schemes:";

    export type SCHEME_Result = 'urn:intarsys:names:conversation:1.0:schemes:Result';
    export type SCHEME_HttpRedirect = 'urn:intarsys:names:conversation:1.0:schemes:HttpRedirect';
    export type SCHEME_Error = 'urn:intarsys:names:conversation:1.0:schemes:Error';
    export type SCHEME_Cancel = 'urn:intarsys:names:conversation:1.0:schemes:Cancel';
    export type SCHEME_Processing = 'urn:intarsys:names:conversation:1.0:schemes:Processing';
    export type SCHEME_RequestInput = 'urn:intarsys:names:conversation:1.0:schemes:RequestInput';
    export type SCHEME_Idle = 'urn:intarsys:names:conversation:1.0:schemes:Idle';

    export type SCHEME = SCHEME_Result | SCHEME_HttpRedirect | SCHEME_Error | SCHEME_Cancel |
        SCHEME_Processing | SCHEME_Idle | SCHEME_RequestInput;

    export interface IHasSnapshot {
        snapshot: ISnaphot;
    }

    export interface ISnaphot {
        conversation: string;
        stage: {
            error?: {
                code: string,
                message: string
            },
            specs?: string,
            name?: string;
            id: number,
            outOfBand?: boolean;
            url: string;
            result?: {
                value: {
                    signatures: ITransportDocument[]
                };
            },
            scheme: SCHEME
        };
    }

    /** Transport Document for posting Docs to sign, getting singed doc */
    export interface ITransportDocument {
        // So far we support only Doc with Transport
        type: "d";
        /** Name of Doc with exension */
        name: string;
        properties: {
            tags: {
                args?: {
                    documentSigner: {
                        args: {
                            decorator: {
                                args: IDecoratorArgs
                            }
                        }
                    }
                }
            }
        };
        /** base 64 encoded Content  */
        content: string;
    }

    export interface IToSignDocument extends ITransportDocument {

        /** # of Previous Signatures in Document */
        PreviousSignatureCount: number;

        // Initialisierung\n03.07.2015
        Text: string;

        Info: {
            Name: string;
            Reason: string;
            Location: string;
            ContactInfo: string;
        };

        /** IconData to Attach (resolved internally from PreviousSignatureCount) */
        IconDataBase64?: string;
    }

    export interface IGearsConfiguration {
        Url: string;
        Configuration?: string;
    }

    // scheme=urn:intarsys:names:conversation:1.0:schemes:HttpRedirect
    export class GearsConnectorService {
        static $inject = ["$injector", "GearsConfiguration"];
        static currentOperation: PromiseLike<any>;

        constructor(
            $injector: ng.auto.IInjectorService,
            private GearsConfiguration: IGearsConfiguration | PromiseLike<IGearsConfiguration> | (() => (IGearsConfiguration | PromiseLike<IGearsConfiguration>)),
        ) {
            this.$c = $injector.get("CompatibilityService");
            this.$window = $injector.get("$window");
        }
        private readonly $c: CompatibilityService;
        private readonly $window: ng.IWindowService;

        public ModalSignDocument<TSignDoc extends IKWFSignDoc>(signOptions: IKWFSignOptions, _signDocs: TSignDoc[] | PromiseLike<TSignDoc[]>) {
            const $c = this.$c;
            return $c.when(_signDocs).then(signDocs => {
                return this.StartSignDocuments(signDocs).then(snapshot => {
                    const r = snapshot.stage.result,
                        signedDocs = r && r.value && r.value.signatures;

                    if (!(signedDocs && signedDocs.length))
                        throw new Error("Keine Signierte Dokumente erhalten!");

                    return signedDocs.map((s, idx) => {
                        const prev = signDocs[idx],
                            changed: Partial<IKWFSignDoc> = {
                                Base64Content: s.content,
                                PreviousSignatureCount: (prev.PreviousSignatureCount >= 0) ? (prev.PreviousSignatureCount + 1) : undefined
                            };
                        return <TSignDoc>$.extend({}, prev, changed);
                    });
                });
            });
        }

        public StartSignDocuments(documents: IKWFSignDoc[], configuration: string | PromiseLike<string> = "") {
            const $c = this.$c;
            const typeRef = GearsConnectorService;
            if (typeRef.currentOperation)
                return $c.reject("Bitte warten Sie die laufende SignaturOperation ab!");

            if (!(documents && documents.length > 0))
                return $c.reject("Keine Dokumente zum Signieren übergeben");

            var getTransportDocuments = $c.all(documents.map(doc => {
                const PreviousSignatureCount = doc.PreviousSignatureCount;
                return getDocDecoratorArgs($c, PreviousSignatureCount, doc.Text).then(docDecorator => {
                    return <ITransportDocument>{
                        type: "d",
                        name: doc.Name,
                        content: doc.Base64Content,
                        properties: {
                            tags: {
                                args: {
                                    documentSigner: {
                                        args: {
                                            decorator: {
                                                args: docDecorator
                                            },
                                            additionalInfoSet: doc.Info,
                                            field: getDocDecoratorField(PreviousSignatureCount)
                                        }
                                    }
                                }
                            }
                        }
                    };
                });
            }));

            var result = typeRef.currentOperation =
                this.$c.when(configuration || this.getConfiguration().then(c => c.Configuration)).then(resolvedConfiguration =>
                    this.$c.when(getTransportDocuments).then(transportDocuments =>
                        this.$c.finally(this.start({
                            configuration: resolvedConfiguration,
                            documents: transportDocuments,
                            args: flattenToBeanProps({
                                documentSigner: {
                                    args: {
                                        decorator: {
                                            factory: "de.intarsys.security.document.type.pdf.signature.ExtendedDecoratorFactory",
                                        },
                                    }
                                }
                            })
                        }).then(data => this.continue(data)), () => {
                            if (result === typeRef.currentOperation)
                                delete typeRef.currentOperation;
                        })
                    )
                );
            return result;
        }

        private start(data: any) {
            return this.getConfiguration().then(config => {
                return this.buildUrl("/signer/create").then(resolvedUrl => {
                    return this.$c.post<{ snapshot: ISnaphot }>(resolvedUrl, data).then(d => d.data && d.data.snapshot, err => {
                        const gearsErrorObj = err && err.data && err.data._error;
                        const gearsError = gearsErrorObj && gearsErrorObj.message;
                        if (gearsError && typeof gearsError === "string") {
                            throw new Error("Fehler bei der Signatur über Gears\r\n:" + gearsError);
                        }
                        throw new Error(`Signatur über Gears- Url "${config.Url}" nicht möglich.\r\nWenden Sie sich an Ihren Administrator.\r\nMögliche Ursache : Die angegebene Url ist nicht korrekt, nicht von Ihrem Client aus erreichbar oder der Gears Server ist nicht gestartet.\r\nDetails:\r\n` + fn.resolveToError(err));
                    });
                });
            });
        }

        private continue(snapshot: ISnaphot): PromiseLike<ISnaphot> {
            try {
                const stage = snapshot && snapshot.stage;
                var scheme = stage && stage.scheme;
                if (!scheme)
                    throw new Error("Expected Scheme");

                if (!(scheme.indexOf(SCHEMA_PREFIX) === 0))
                    throw new Error(`Expected Scheme '${SCHEMA_PREFIX}', got '${scheme}'`);

                switch (scheme) {
                    case "urn:intarsys:names:conversation:1.0:schemes:HttpRedirect":
                        return this.continueRedirect(snapshot);
                    case "urn:intarsys:names:conversation:1.0:schemes:Result":
                        return this.$c.when(snapshot);
                    case "urn:intarsys:names:conversation:1.0:schemes:Cancel":
                        fn.error.ThrowCancel();
                    case "urn:intarsys:names:conversation:1.0:schemes:Error":
                        throw new Error("Snapshot continue, got Error:" + ((stage.error && stage.error.message) || scheme));
                    case "urn:intarsys:names:conversation:1.0:schemes:Processing":
                        var result = this.$c.defer<ISnaphot>();
                        this.$window.setTimeout(() => {
                            this.acknowledge(snapshot).then(result.resolve, result.reject);
                        }, 2500);
                        return result.promise;
                    case "urn:intarsys:names:conversation:1.0:schemes:RequestInput":
                        return this.$c.when(this.RequestInput()).then((inputValue) => {
                            if (!inputValue) {
                                throw new Error('User Input Expected, got none');
                            }
                            return this.acknowledge(snapshot, inputValue);
                        });

                    case "urn:intarsys:names:conversation:1.0:schemes:Idle":
                        throw new Error("Not Implemented:" + scheme);

                    default:
                        fn.never(scheme, "Scheme not implemented: " + scheme);
                }
            } catch (err) {
                return this.$c.reject(err);
            }
        }

        public RequestInput(): PromiseLike<any> | any {
            return this.$c.reject("User Input Requested");
        }

        private continueRedirect(snapshot: ISnaphot) {
            const stage = snapshot && snapshot.stage;
            if (!(stage && stage.url))
                throw new Error("No Url!");

            var name = stage.name || "_blank";
            var specs = stage.specs || "location=no,menubar=no,status=no,toolbar=no,width=800,height=400";

            var wnd = window.open(stage.url, name, specs);
            if (!wnd)
                throw new Error("Signaturfenster kann nicht geöffnet werden, u.U. verhindert der Popup- Blocker Ihres Browser das Öffnen des Fensters!");

            var result = this.$c.defer();
            var i = this.$window.setInterval(() => {
                try {
                    if (wnd && wnd.closed) {
                        result.resolve("Window got closed");
                    }
                } catch (err) {
                    result.reject("Err:" + err);
                }
            }, 1500);

            this.$c.finally(result.promise, () => this.$window.clearInterval(i));
            return result.promise.then(() => this.acknowledge(snapshot));
        }

        private acknowledge(snapshot: ISnaphot, resultValue?: any) {
            const stage = snapshot && snapshot.stage;
            if (!stage)
                return this.$c.reject("Missing snapshot/stage");

            return this.buildUrl("/conversation/acknowledge").then(resolvedUrl => {
                return this.$c.post<IHasSnapshot>(resolvedUrl, {
                    conversation: snapshot.conversation || {},
                    inReplyTo: stage.id,
                    value: resultValue || {}
                }).then(s => this.continue(s.data && s.data.snapshot));
            });
        }

        private buildUrl(relativeUrl: string) {
            return this.getConfiguration().then(configuration => {
                return configuration.Url + relativeUrl;
            });
        }

        private getConfiguration() {
            var gears = this.GearsConfiguration;
            if (fn.isFunction(gears)) {
                gears = gears();
            }

            return this.$c.when(gears).then((configuration: (IGearsConfiguration & { _Validated: boolean })) => {
                if (configuration && configuration._Validated)
                    return configuration;
                let rawUrl = configuration && configuration.Url;
                if (!rawUrl) {
                    throw new Error("Invalid Gears Configuration:" + JSON.stringify(configuration || ""));
                }
                configuration.Url = rawUrl = rawUrl.replace(/\/+$/, "");
                // Normalize Gears access url
                var parts = rawUrl.toLocaleLowerCase().split("/");
                const checkParts = "cloudsuite-gears/core/api/v1/flow".split('/');
                fn.FindFirst(checkParts, ((part, idx) => {
                    if (rawUrl.toLocaleLowerCase().indexOf(part) < 0) {
                        var toAdd = checkParts.slice(idx).join('/');
                        return !!(configuration.Url = rawUrl + "/" + toAdd);
                    }
                }));

                configuration._Validated = true;
                return this.GearsConfiguration = configuration;
            });
        }
    }

    /** Transform Applet Arguments Objects in custom Applet Argument Syntax */
    function flattenToBeanProps(objArgs: Object) {
        function iterateLeafs(curr: any, cb: (curr: { Name: string; Value: any; Path: string[] }) => void, p?: { Path: string[] }) {
            p = p || { Path: [] };
            function addedPath(name: string | number, cb: () => void) {
                p.Path.push("" + name);
                cb();
                p.Path.pop();
            }

            var name;
            for (name in curr) {
                addedPath(name, () => {
                    var val = curr[name];
                    if ($.isPlainObject(val)) {
                        iterateLeafs(val, cb, { Path: p.Path });
                    }
                    else if ($.isArray(val)) {
                        val.forEach((item, idx) => {
                            addedPath(idx, () => iterateLeafs(item, cb, p));
                        });
                    } else if (val !== undefined) {
                        cb({ Name: name, Path: p.Path, Value: val });
                    }
                });
            }
        }

        /** Custom Encoding for Applet Params : NewLine => ${entity.nl}, ... */
        function encodeValue(val) {
            var result = ("" + val).replace("/\$/g", "\$").replace(/\n|\r\n|\r/g, "${entity.nl}");
            return result;
        }

        var result = {};
        iterateLeafs(objArgs, x => result[x.Path.join(".")] = encodeValue(x.Value));
        return result;

    }

    angular.module("infoma.gears", [])
        .service("GearsConnector", GearsConnectorService);

}

export namespace applet {

    /** Transform Applet Arguments Objects in custom Applet Argument Syntax */
    function getArgsAsString(this: void, objArgs: Object) {
        function iterateLeafs(curr: any, cb: (curr: { Name: string; Value: any; Path: string[] }) => void, p?: { Path: string[] }) {
            p = p || { Path: [] };
            function addedPath(name: string | number, cb: () => void) {
                p.Path.push("" + name);
                cb();
                p.Path.pop();
            }
            var name;
            for (name in curr) {
                addedPath(name, () => {
                    var val = curr[name];
                    if ($.isPlainObject(val)) {
                        iterateLeafs(val, cb, { Path: p.Path });
                    }
                    else if ($.isArray(val)) {
                        val.forEach((item, idx) => {
                            addedPath(idx, () => iterateLeafs(item, cb, p));
                        });
                    } else if (val !== undefined) {
                        cb({ Name: name, Path: p.Path, Value: val });
                    }
                });
            }
        }

        /** Custom Encoding for Applet Params : NewLine => ${entity.nl}, ... */
        function encodeValue(val) {
            var result = ("" + val).replace("/\$/g", "\$").replace(/\n|\r\n|\r/g, "${entity.nl}");
            return result;
        }

        var result = [];
        iterateLeafs(objArgs, x => result.push(x.Path.join(".") + "=" + encodeValue(x.Value)));
        return result.join(";");
    }

    export function ResolveAppletDocInfos(this: void, $c: CompatibilityService, newVal: IKWFSignDoc[], SignOptions: IKWFSignOptions, defaultSignatureSettings: any) {
        var loadIconData = newVal.map(item => GetSignIcon($c, item.PreviousSignatureCount).then(iconData => item.IconDataBase64 = iconData));
        return $c.all(loadIconData).then(() => {
            var appArgs = $.extend({}, defaultSignatureSettings || {});
            fn.ExtendObject(appArgs, {
                log: {
                    // filename: "%h/CloudSuite_%u_%g.log",
                    // level: "INFO"
                },
                principalFilter: "principal.supportsSignature",
                deviceProvider: {
                    composite: {
                        accumulate: true
                    }
                },
                autocommit: true,
                documents: newVal.map(doc => {
                    return {
                        name: doc.Name,
                        documentSignerArgs: {
                            field: {
                                create: true,
                                position: S_Dim.GetPos(doc.PreviousSignatureCount || 0),
                                size: S_Dim.GetSize(),
                                pageRange: "last",
                            },
                            decorator: "com.cabaret.security.method.pdf.signing.decorator.ExtendedDecoratorFactory",
                            decoratorArgs: {
                                textScaleWhen: "always",
                                textScaleProportional: true,
                                textVAlign: "center",
                                textHAlign: "right",

                                layout: "textRightOfIcon",
                                text: doc.Text || "",
                                icon: doc.IconDataBase64 && {
                                    name: "srr.png",
                                    content: doc.IconDataBase64
                                },
                                iconScaleWhen: "always",
                                iconScaleProportional: true,
                                iconVAlign: "center",
                                iconHAlign: "left",
                            },
                            additionalInfoSet: {
                                Name: doc.Info.Name || "",
                                Reason: doc.Info.Reason || "",
                                Location: doc.Info.Location || "",
                                ContactInfo: doc.Info.ContactInfo || ""
                            }
                        },
                    };
                })
            });
            return appArgs;
        });
    }

    export class AppletConnector {
        static $inject = ["CompatibilityService", "NavSettings", "DialogService"];
        constructor(
            private $c: CompatibilityService,
            private NavSettings: NavSettingsService,
            private DialogService: DialogService) {
        }

        public ModalSignDocument<TSignDoc extends IKWFSignDoc>(signOptions: IKWFSignOptions, signDocs: TSignDoc[] | PromiseLike<TSignDoc[]>) {
            var showWindow = this.IsJavaInstalled().then((javaInstalled) => {
                if (javaInstalled == false) {
                    checkJava = this.NavSettings.UserConfirmIgnore({ Token: "IgnoreJavaWarning", Area: "KWF" }, (s) => {
                        if (s.HasValue && s.CurrValue)
                            return;

                        var ignore: IMessageIgnore = {};
                        return this.DialogService.ShowDialog({
                            title: "Warnung",
                            Ignore: ignore,
                            message: "Für die Signaturfunktionalität muss Java(1.7+) installiert und aktiviert sein; \ndie notwendige Java Version konnte nicht validiert werden!"
                        }).then(() => {
                            if (ignore.IsIgnored) {
                                s.CurrValue = s.HasValue = true;
                            }
                            return s;
                        });
                    });
                }
                else
                    var checkJava = <PromiseLike<any>>this.$c.when<any>(undefined);

                return checkJava.then(() => this.DialogService.ShowWindow({
                    controller: "CtrlKWFSign",
                    template: "<div><kwf_Signature></kwf_Signature></div>",
                    resolve: {
                        "SignDoks": fn.ToFunc(signDocs),
                        "SignOptions": fn.ToFunc(signOptions)
                    },
                    windowOptions: {
                        modal: true,
                        resizable: true,
                        actions: ['Maximize', 'Close']
                    }
                }).then((w) => {
                    let sScope = <IKWFSignScope>w.scope;
                    function destroyApplet(withClose?: boolean) {
                        var wnd = w && w.window;
                        if (wnd) {
                            // We must tear Down applet this way, a simple remove from DOM might reCreate ... unspecific errors
                            var applet = $("applet", wnd.wrapper)[0];
                            if (applet) {
                                applet.outerHTML = "<div> ... <div>";
                            }
                            if (withClose) {
                                wnd.close();
                            }
                        }
                    }
                    var explicitClose = "";
                    sScope.$on("SignatureEnd", (e, arg) => {
                        explicitClose = arg || "unkown";
                        destroyApplet(true);
                    });
                    sScope.$on("SignatureProgress", (e, arg) => {
                        if (arg == "done") { explicitClose = "ok"; destroyApplet(true); }
                        if (arg == "active") { w.window.center(); } // as all previous center action might take place on not yet fully initialized window
                    });

                    w.window.bind("close", () => {
                        destroyApplet();
                        if (explicitClose == "cancel" || explicitClose == "")
                            result.resolve(); // graceful cancellation
                        else if (explicitClose == "fail")
                            result.reject("Fehler beim Signieren: Applet wurde mit Fehler beendet");
                        else {
                            try {
                                var signData = <TSignDoc[]>sScope.GetSignatureInfo();

                                if (!signData || signData.length == 0)
                                    throw new Error("No Signature Result given!");
                                if (!signData.every(item => !!item.Base64Content))
                                    throw new Error("Signatur abgebrochen!");
                                result.resolve(signData);
                            } catch (err) {
                                result.reject(err);
                            }
                        }
                    });
                })
                );
            });

            var result = this.$c.defer<TSignDoc[]>();

            showWindow.then(undefined, err => result.reject(err));

            return result.promise;
        }

        /** Check jf Java is Installed (returns true, false or undefined! */
        public IsJavaInstalled(strVersion: string = '1.7+') {
            let info = AppletConnector._javaInfo;
            if (strVersion in info)
                return info[strVersion].promise;

            if (typeof (require) != "function")
                return this.$c.when(<boolean>undefined);

            if (navigator && typeof (navigator.javaEnabled) == "function" && !navigator.javaEnabled())
                return this.$c.when(navigator.javaEnabled());

            var result = info[strVersion] = this.$c.defer<boolean>();
            const root = this.$c.resolve("WebApiRoot").WebApiRoot || "./";
            const jsUrl = root + '/scripts/util/deployJava.min.js';
            $.getScript(jsUrl).then(() => {
                try {
                    var deployJava: { getJREs: () => any[], versionCheck(v: string): boolean } = window["deployJava"];
                    if (deployJava && deployJava.versionCheck) {
                        var checkJava = deployJava.versionCheck(strVersion);
                        result.resolve(checkJava);
                    }
                    result.resolve(undefined);
                } catch (err) {
                    result.resolve(undefined);
                }
            }, (err) => {
                // not loaded
                result.resolve(undefined);
            });
            // Java Check Object will interfer with UI otherwise...
            this.$c.finally(result.promise, () => $("#deployJavaPlugin").hide());
            return result.promise;
        }
        private static _javaInfo: { [version: string]: IDeferred<boolean> } = {};
    }

    /**  Digitale Signatur */
    var currentSignScope: IKWFSignScope;
    export function UIFinish(arg: string, id: any) {
        if (currentSignScope && (currentSignScope.ID == id)) {
            currentSignScope.UIFinish(arg);
        }
        //alert("UIFinish:" + arg + ", Signature='" + $("#signature").val() + "', Certificate='" + $("#certificate").val() + "', StackTrace='" + $("#stacktrace").val() + "'");
    }
    export function UIProgress(arg: string, id: any) {
        if (currentSignScope && currentSignScope.ID == id) {
            currentSignScope.UIProgress(arg);
        }
    }
    var CtrlKWFSign = ["$scope", "DialogService", "SignDoks", "SignOptions", function ($scope: IKWFSignScope, DialogService: DialogService,
        signDoks: IKWFSignDoc[], signOptions: IKWFSignOptions) {

        $scope["SignDocs"] = signDoks;
        $scope.SignOptions = signOptions;
        $scope.ID = fn.id();
        var finalize = () => {
            if (currentSignScope == $scope)
                currentSignScope = undefined;
        };

        currentSignScope = $scope;
        $scope.UIFinish = (arg) => $scope.$broadcast("SignatureEnd", arg);
        $scope.UIProgress = (arg) => $scope.$broadcast("SignatureProgress", arg);
        // Get result by Cloning Input and ReAssign Changed Items
        $scope.GetSignatureInfo = () => {
            return signDoks.map((item, idx) => {
                item = $.extend({}, item);
                item.Base64Content = $("#signatures_" + idx).val() || "";
                item.PreviousSignatureCount = item.PreviousSignatureCount + 1;
                item["StackTrace"] = $("stacktrace").val() || "";
                return item;
            });

        };
        $scope.$on("$destroy", finalize);
    }];

    var kwfSignatureApplet = ["$compile", "CompatibilityService", "RwfAppSettings", function (
        $compile: ng.ICompileService,
        $c: CompatibilityService,
        RwfAppSettings: RwfAppSettings): ng.IDirective {
        return <ng.IDirective>{
            transclude: true,
            link: (
                scope: IKWFSignScope,
                instanceElement: ng.IAugmentedJQuery,
                instanceAttributes: ng.IAttributes,
                controller: any,
                transclude: ng.ITranscludeFunction
            ) => {

                const root = $c.resolve("WebApiRoot").WebApiRoot || "./";
                scope.$watch<IKWFSignDoc[]>("::SignDocs", (newVal) => {
                    UISafe(RwfAppSettings.Get().then(c => {
                        const SignOptions = scope?.SignOptions;
                        return ResolveAppletDocInfos($c, newVal, SignOptions, c.DefaultSignatureSettings).then(docInfos => {
                            var id = scope.ID,
                                formId = id;
                            var dummy = applet.UIFinish;
                            const jPrefix = "infoma.sign.applet";
                            var appletAttributes = {
                                code: "de.intarsys.cloudsuite.applet.control.workflow.SignatureApplet",
                                ARG_FORMS: formId,
                                codebase: root + "KWF/Signatur/CloudSuiteApplet",
                                width: "900",
                                height: "600",
                                jnlp_href: "./pdfsignature.jnlp",
                                image: "splash.png",
                                boxbgcolor: "#ffffffff",
                                boxborder: "false",
                                centerimage: "true",
                                on_cancel: `javascript:${jPrefix}.UIFinish('cancel', '${id}')`,
                                on_ok: `javascript:${jPrefix}.UIFinish('ok', '${id}')`,
                                on_fail: `javascript:${jPrefix}.UIFinish('fail', '${id}')`,
                                on_active: `javascript:${jPrefix}.UIProgress('active', '${id}')`,
                                on_done: `javascript:${jPrefix}.UIProgress('done', '${id}')`,
                                ARGS_0: getArgsAsString(docInfos)
                            };
                            if (SignOptions && SignOptions.SerialNo)
                                appletAttributes["PRINCIPAL_FILTER"] = "principal.serialNumber=='" + SignOptions.SerialNo + "'";

                            var appletFormTemplate = `<form id='${formId}'>` + Applet.getAppletStr(appletAttributes);

                            // Add hidden Fields (for parameter injection to applet)
                            newVal.forEach((item, idx) => {
                                appletFormTemplate += "<input name='documents_" + idx + "_name' type='hidden' id='documents_" + idx + "_name' value='{{::SignDocs[" + idx + "].Name}}' />" +
                                    "    <input name='documents_" + idx + "_content' type='hidden' id='documents_" + idx + "_content' value='{{::SignDocs[" + idx + "].Base64Content}}' />" +
                                    "    <input name='signatures_" + idx + "' type='hidden' id='signatures_" + idx + "' />";
                            });

                            appletFormTemplate += "<input type='hidden' name='certificate' id='certificate' />" +
                                "<input type='hidden' name='stacktrace' id='stacktrace' />" +
                                "</form>";

                            var compiled = $compile(appletFormTemplate)(scope);

                            var elem = compiled.appendTo(instanceElement);
                            // WOut : Error log on desktop on IE Window Close after Signing (don't know why ...)
                            scope.$on("$destroy", () => elem.remove());
                        });
                    }));
                });
            }
        };
    }];

    namespace Applet {
        /** HTML attribute filter implementation */
        var hattrs = {
            core: ['id', 'class', 'title', 'style'],
            i18n: ['lang', 'dir'],
            events: ['onclick', 'ondblclick', 'onmousedown', 'onmouseup',
                'onmouseover', 'onmousemove', 'onmouseout', 'onkeypress',
                'onkeydown', 'onkeyup'],
            applet: ['codebase', 'code', 'name', 'archive', 'object',
                'width', 'height', 'alt', 'align', 'hspace', 'vspace'],
            object: ['classid', 'codebase', 'codetype', 'data', 'type',
                'archive', 'declare', 'standby', 'height', 'width', 'usemap',
                'name', 'tabindex', 'align', 'border', 'hspace', 'vspace']
        };
        var object_valid_attrs = hattrs.object.concat(hattrs.core, hattrs.i18n,
            hattrs.events);
        var applet_valid_attrs = hattrs.applet.concat(hattrs.core);
        function isValidAppletAttr(attr) {
            return arHas(applet_valid_attrs, attr.toLowerCase());
        }
        function arHas(ar, attr) {
            var len = ar.length;
            for (var i = 0; i < len; i++) {
                if (ar[i] === attr) return true;
            }
            return false;
        }
        export function getAppletStr(attributes: any, parameters?: Object) {
            var startApplet = '<' + 'applet ';
            var params = '';
            var endApplet = '<' + '/' + 'applet' + '>';
            var addCodeAttribute = true;

            if (null == parameters || typeof parameters != 'object') {
                parameters = new Object();
            }

            for (var attribute in attributes) {
                if (!isValidAppletAttr(attribute)) {
                    parameters[attribute] = attributes[attribute];
                } else {
                    startApplet += (' ' + attribute + '="' + attributes[attribute] + '"');
                    if (attribute == 'code') {
                        addCodeAttribute = false;
                    }
                }
            }

            var codebaseParam = false;
            for (var parameter in parameters) {
                if (parameter == 'codebase_lookup') {
                    codebaseParam = true;
                }
                // Originally, parameter 'object' was used for serialized
                // applets, later, to avoid conBifusion with object tag in IE
                // the 'java_object' was added.  Plugin supports both.
                if (parameter == 'object' || parameter == 'java_object' ||
                    parameter == 'java_code') {
                    addCodeAttribute = false;
                }
                params += '<param name="' + parameter + '" value="' +
                    parameters[parameter] + '"/>';
            }
            if (!codebaseParam) {
                params += '<param name="codebase_lookup" value="false"/>';
            }

            if (addCodeAttribute) {
                startApplet += (' code="dummy"');
            }
            startApplet += '>';

            return startApplet + '\n' + params + '\n' + endApplet;
        }
    }

    angular.module("KWF.Sign", [])
        .directive("kwfSignature", kwfSignatureApplet)
        .controller("CtrlKWFSign", CtrlKWFSign)
        ;
}

export class CtrlTestSignature extends Observable {
    public static Fragment = require('./infoma.sign.test.html')
    public static Route: IKWFRoute = {
        DescriptionFormula: '"Test der Signaturfunktionalität"',
        controller: CtrlTestSignature,
        controllerAs: 'vm',
        template: CtrlTestSignature.Fragment,
    }
    static Directive = [(): ng.IDirective => {
        return {
            link: (scope, element) => {
                (scope["vm"] as CtrlTestSignature)?.AttachInput(element[0] as HTMLInputElement)
            }
        };
    }]
    static TEST_PDF_CONTENT = "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";

    static $inject = ["CompatibilityService", "$injector"];
    constructor(private $c: CompatibilityService, $injector: ng.auto.IInjectorService) {
        super();
        $c.resolve("Rwf")("RwfBootstrap").Resolve;
        this.TestSignature = {
            Execute: async () => {
                try {
                    this.TestSignature.IsRunning = true;
                    var $rwfBootstrap = $c.resolve("Rwf")("RwfBootstrap").Resolve;
                    const $rwfUser = await $rwfBootstrap.then(r => r.RwfUser);
                    var e = { Base64Content: $c.when(CtrlTestSignature.TEST_PDF_CONTENT) };
                    this.trigger("SelectDoc", e);
                    const Base64Content = await e.Base64Content;

                    const signDoc: IKWFSignDoc = {
                        Base64Content: Base64Content,
                        PreviousSignatureCount: 0,
                        Name: "TestSignatur.pdf",
                        Text: "Text",
                        Info: {
                            ContactInfo: $rwfUser.E_Mail || "",
                            Location: "Location",
                            Name: $rwfUser.ID,
                            Reason: "Reason"
                        }
                    }

                    type OptPromisified<T> = { [name in keyof T]: T[name] | PromiseLike<T[name]> };
                    return await $c.all(<OptPromisified<IKWFSignOptions>>{
                        SerialNo: $rwfBootstrap.then(bootstrap => {
                            const user = bootstrap.RwfUser;
                            if (bootstrap.RwfSetup.SignatureSerialNoMandatory && !user.SignatureSerialNumber)
                                ThrowGracefullError("Signieren ohne hinterlegte Signaturkarteninformation/AliasInformation nicht möglich!");
                            return user.SignatureSerialNumber;
                        }),
                        Bridge: $c.get<string>("KWF/Signatur/bridge.lic?ver=" + fn.id()).then(r => r.data && <IBrigdeOptions>{
                            LicenseBase64: btoa(r.data),
                            Select: $rwfBootstrap.then(bootstrap => {
                                // TODO only valid for windows auth?
                                const userName = bootstrap.RwfSettings.User.LoginName;
                                const userfrags = userName.split('\\');
                                return {
                                    user:(userfrags.length > 0 ? userfrags[userfrags.length - 1] : userName).toLowerCase(),
                                };
                            })
                        }, fn.noop),
                        ProGOV: $rwfBootstrap.then(bootstrap => {
                            return bootstrap.RwfSettings.ProGOV;
                        })
                    }).then(r => {
                        return ModalSignDocument($injector, r, [signDoc]).then((docs: IKWFSignDoc[]) => {
                            if (docs?.length > 0) {
                                const currResultDoc = base64toBlob(docs[0].Base64Content, "application/pdf");
                                downloadFile(currResultDoc, docs[0].Name + "_" + fn.id() + ".pdf");
                            }
                        });
                    });
                } finally {
                    this.TestSignature.IsRunning = false;
                    $c.tick();
                }
            }
        }

        this.ReAuthenticate = {
            Execute: () => {
                return ReAuthenticate($c).then(() => {
                    $c.resolve("DialogService").ShowDialog("Benutzerauthentifizierung erfolgreich!");
                });
            }
        }
    }

    AttachInput(input: HTMLInputElement) {
        input.onchange = async (e) => {
            try {
                if (typeof window.FileReader !== 'function')
                    throw ("The file API isn't supported on this browser.");


                if (!input.files)
                    throw ("This browser does not support the `files` property of the file input.");
                if (!input.files[0])
                    throw ("File Selection Cancelled");

                let file = input.files[0];

                var result = await toBase64(file);
                result = result.split("base64,")[1] || result;
                this.trigger("FileSelected", { Base64Content: result })
            } catch (err) {
                this.trigger("FileSelected", { Base64Content: this.$c.reject(err) })
            }
        }
        this.bind("SelectDoc", (e) => {
            var orig = e.Base64Content;
            if (orig) {
                e.Base64Content = new Promise<string>(res => this.one("FileSelected", e => res(e.Base64Content)));
                $(input).click();
            }
        });
    }

    TestSignature: IInfOperation;

    ReAuthenticate: IInfOperation;
}


angular.module("KWF.Sign")
    .directive("testsignatureinput", CtrlTestSignature.Directive);
